import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { createConfluxPaymentOrder, payOrder } from "@/api/mall.js";
import { useSiteTemplate } from "@/composables/useSiteTemplate";
import { useJapanStyleSite } from "@/composables/useJapanStyleSite";
import { useMainStore } from "@/composables/store";
import { resolveTemplateCheckoutConfig } from "@/utils/templateCheckout";
import { saveJapanPendingPaymentRedirect } from "@/utils/japanPendingPaymentRedirect";
import { resolveSiteDisplayTitle } from "@/utils/siteSeo";
import {
  CONFLUX_CARD_PAYMENT_CODES,
  normalizePaymentCode,
  resolveConfluxCreditCardPaymentMethod,
  resolveConfluxPaymentResult,
} from "@/utils/confluxPayment";
import { JAPAN_PREFECTURE_LIST, normalizeJapanPhone } from "@/utils/japanAddress";
import { $t } from "@/utils/index.js";

function normalizeValue(value) {
  if (value === null || typeof value === "undefined") return "";
  return `${value}`.trim();
}

function pickOrderId(order = {}) {
  return normalizeValue(
    order.id
    || order.dealerOrderPrimaryId
    || order.dealerOrderId
    || order.orderPrimaryId
    || order.orderId
  );
}

function pickOrderCode(order = {}) {
  return normalizeValue(
    order.orderCode
    || order.mallOrderCode
    || order.bizOrderCode
  );
}

function pickOrderNumber(order = {}) {
  return normalizeValue(
    order.dealerOrderPrimaryId
    || order.id
    || order.orderId
    || order.mallOrderId
    || order.orderNumber
  );
}

function pickPayOrderCode(order = {}) {
  return normalizeValue(
    order.payOrderCode
    || order.mallPayOrderCode
    || order.paymentOrderCode
    || order.payOrderNo
    || order.bizTradeId
  );
}

function pickOrderEmail(order = {}) {
  return normalizeValue(
    order.email
    || order.userEmail
    || order.customerEmail
    || order.shippingInfo?.email
    || order.cityObj?.email
  );
}

function pickOrderMobile(order = {}) {
  return normalizeValue(
    order.mobileNo
    || order.phone
    || order.userMobile
    || order.customerMobile
    || order.shippingInfo?.userMobile
    || order.cityObj?.userMobile
    || order.cityObj?.mobileNo
  );
}

function getRouteQueryValue(route, key) {
  const value = route?.query?.[key];
  if (Array.isArray(value)) return value[0] || "";
  return normalizeValue(value);
}

function readStoredOrderInfo() {
  if (!process.client) return {};

  try {
    return JSON.parse(localStorage.getItem("orderInfo") || "{}") || {};
  } catch (error) {
    return {};
  }
}

function getOrderDetails(order = {}) {
  const detailPaths = [
    "dealerOrderDetailDTOList",
    "dealerOrderDetailList",
    "mallOrderDetailDTOList",
    "orderDetailDTOList",
    "orderDetailList",
    "detailList",
    "goodsList",
    "goodsItemList",
    "itemList",
    "items",
    "skuList",
    "orderItemList",
    "dealerOrderDTO.dealerOrderDetailDTOList",
    "mallOrderDTO.dealerOrderDetailDTOList",
  ];

  for (const path of detailPaths) {
    const value = path.split(".").reduce((target, key) => target?.[key], order);
    if (Array.isArray(value) && value.length) return value;
  }

  return [];
}

function normalizeNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function mapOrderDetailToCartItem(item = {}, index = 0, order = {}, fallbackBrand = "") {
  const qty = normalizeNumber(
    item.buyAmount
    ?? item.buyNum
    ?? item.qty
    ?? item.quantity
    ?? item.count,
    1
  );
  const totalAmount = normalizeNumber(
    item.realSubtotalAmount
    ?? item.originalSubtotalAmount
    ?? item.totalAmount
    ?? item.payableAmount,
    0
  );
  const unitPrice = normalizeNumber(
    item.originalUnitPrice
    ?? item.realUnitPrice
    ?? item.preSalePrice
    ?? item.salePrice
    ?? item.unitPrice
    ?? item.price
    ?? item.amount
    ?? (qty > 0 ? totalAmount / qty : 0),
    0
  );
  const title = normalizeValue(
    item.dealerItemSpuTitle
    || item.dealerItemSkuTitle
    || item.spuName
    || item.goodsName
    || item.productName
    || item.itemName
    || item.title
    || item.subject
  ) || $t("payment.product");
  const iconUrl = normalizeValue(
    item.dealerItemSpuIconUrl
    || item.spuIconUrl
    || item.iconUrl
    || item.image
    || item.imageUrl
    || item.goodsImage
    || item.productImage
    || item.picUrl
  );

  return {
    id: item.dealerItemSkuId || item.dealerItemSkuIdStr || item.skuId || item.id || `pending_item_${index}`,
    title,
    brand: item.dealerShopName || item.shopName || order.dealerShopName || fallbackBrand,
    price: unitPrice,
    qty,
    iconUrl,
    options: item.spItemSkuSpecConfig || item.skuSpec || item.options || "",
    offerId: item.dealerItemSkuId || item.skuId || item.id || "",
    realSubtotalAmount: totalAmount || unitPrice * qty,
    dealerShopId: item.dealerShopId || order.dealerShopId || order.shopId || "",
    dealerItemSpuTitle: title,
    dealerItemSpuIconUrl: iconUrl,
  };
}

function buildPaymentResumeQuery(order = {}, options = {}) {
  const orderCode = pickOrderCode(order);
  const orderNumber = pickOrderNumber(order) || orderCode;
  const payOrderCode = pickPayOrderCode(order);
  const query = {
    pendingPay: "1",
    orderCode,
    orderNumber,
    payOrderCode,
    email: pickOrderEmail(order),
    mobile: pickOrderMobile(order),
    paymentMethodCode: normalizeValue(options.paymentMethodCode || order.paymentMethodCode),
  };

  if (options.publicOrder) {
    query.public = "1";
  }

  return Object.entries(query).reduce((result, [key, value]) => {
    if (value) result[key] = value;
    return result;
  }, {});
}

function persistPaymentResumeSnapshot(order = {}, query = {}, fallbackBrand = "") {
  if (!process.client) return;

  const details = getOrderDetails(order);
  const cartItems = details.map((item, index) => mapOrderDetailToCartItem(item, index, order, fallbackBrand));
  const subtotal = cartItems.reduce((sum, item) => {
    return sum + normalizeNumber(item.realSubtotalAmount || item.price * item.qty, 0);
  }, 0);
  const shippingFee = normalizeNumber(
    order.realTransportationExpenses
    ?? order.originalTransportationExpenses
    ?? order.transportationExpenses
    ?? order.transportationFee
    ?? order.shippingFee,
    0
  );
  const totalAmount = normalizeNumber(
    order.realTotalAmount
    ?? order.payableAmount
    ?? order.totalAmount
    ?? order.orderAmount,
    subtotal + shippingFee
  );

  try {
    const previousInfo = JSON.parse(localStorage.getItem("orderInfo") || "{}") || {};
    localStorage.setItem("orderInfo", JSON.stringify({
      ...previousInfo,
      pendingOrderSnapshot: order,
      orderId: query.orderNumber || previousInfo.orderId || "",
      orderNumber: query.orderNumber || previousInfo.orderNumber || "",
      orderCode: query.orderCode || previousInfo.orderCode || "",
      payOrderCode: query.payOrderCode || previousInfo.payOrderCode || "",
      payOrderNo: query.payOrderCode || previousInfo.payOrderNo || "",
      email: query.email || previousInfo.email || "",
      mobileNo: query.mobile || previousInfo.mobileNo || "",
      totalAmount,
      subtotal: subtotal || Math.max(0, totalAmount - shippingFee),
      shippingFee,
      cartItems: cartItems.length ? cartItems : (Array.isArray(previousInfo.cartItems) ? previousInfo.cartItems : []),
      cityObj: order.cityObj || previousInfo.cityObj || {},
      shippingInfo: {
        ...(previousInfo.shippingInfo || {}),
        ...(order.shippingInfo || {}),
        ...(order.cityObj || {}),
      },
      isGuestCheckout: true,
      isJapanStyleCheckout: true,
      checkoutCurrency: normalizeValue(order.checkoutCurrency || order.currencyCode || order.currency || previousInfo.checkoutCurrency || "JPY"),
    }));
  } catch (error) {
    // Snapshot is only a resume helper; routing can still use query params.
  }
}

function buildAbsoluteCheckoutUrl(path, query = {}) {
  if (!process.client || typeof window === "undefined") return path;

  const url = new URL(path, window.location.origin);
  Object.entries(query || {}).forEach(([key, value]) => {
    const normalizedValue = normalizeValue(value);
    if (normalizedValue) {
      url.searchParams.set(key, normalizedValue);
    }
  });
  return url.toString();
}

function splitConfluxName(value = "") {
  const name = normalizeValue(value);
  if (!name) {
    return {
      first_name: "Guest",
      last_name: "Guest",
    };
  }

  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return {
      first_name: parts.slice(1).join(" "),
      last_name: parts[0],
    };
  }

  return {
    first_name: name,
    last_name: name,
  };
}

const confluxJapanPrefectureCodeMap = JAPAN_PREFECTURE_LIST.reduce((map, item) => {
  if (item?.name && item?.code) {
    map[item.name.replace(/\s+/g, "")] = `JP-${item.code}`;
  }
  return map;
}, {});

function normalizeConfluxCountryCode(value = "") {
  const country = normalizeValue(value || "JP");
  if (!country || country === "日本" || /^japan$/i.test(country) || /^jpn$/i.test(country)) {
    return "JP";
  }
  return country.toUpperCase();
}

function normalizeConfluxJapanState(value = "") {
  const state = normalizeValue(value);
  if (!state) return "";

  const compactState = state.replace(/\s+/g, "");
  if (/^JP-\d{2}$/i.test(compactState)) {
    return compactState.toUpperCase();
  }

  return confluxJapanPrefectureCodeMap[compactState] || state;
}

function normalizePhoneForConflux(value = "") {
  return normalizeJapanPhone(value) || normalizeValue(value).replace(/(?!^\+)[^\d]/g, "");
}

function buildConfluxAddressInfoFromOrder(order = {}, storeUserInfo = {}) {
  const storedOrderInfo = readStoredOrderInfo();
  const shippingInfo = {
    ...(storedOrderInfo.shippingInfo || {}),
    ...(order.shippingInfo || {}),
    ...(order.cityObj || {}),
  };
  const userRealname = normalizeValue(
    order.userRealname
    || order.realname
    || order.name
    || shippingInfo.userRealname
    || storedOrderInfo.realname
    || storedOrderInfo.userRealname
  );
  const country = normalizeConfluxCountryCode(
    order.countryCode
    || shippingInfo.countryCode
    || storedOrderInfo.countryCode
    || storedOrderInfo.country
    || "JP"
  );
  const province = normalizeValue(order.province || order.state || shippingInfo.province || shippingInfo.state || storedOrderInfo.province);
  const city = normalizeValue(order.city || shippingInfo.city || storedOrderInfo.city || province);
  const district = normalizeValue(order.district || shippingInfo.district || storedOrderInfo.district);
  const street = normalizeValue(order.street || shippingInfo.street || storedOrderInfo.street);
  const detailAddress = normalizeValue(
    order.detailAddress
    || order.detail
    || order.address
    || order.siteAddress
    || shippingInfo.detailAddress
    || shippingInfo.detail
    || shippingInfo.address
    || storedOrderInfo.detailAddress
  );
  const addressLine1 = [district, street].filter(Boolean).join(" ") || detailAddress || city || province;
  const addressLine2 = detailAddress && detailAddress !== addressLine1 ? detailAddress : "";
  const addressInfo = {
    ...splitConfluxName(userRealname),
    email: normalizeValue(
      order.email
      || shippingInfo.email
      || storedOrderInfo.email
      || storeUserInfo.email
    ),
    phone: normalizePhoneForConflux(
      order.phone
      || order.mobileNo
      || order.userMobile
      || shippingInfo.userMobile
      || shippingInfo.mobileNo
      || storedOrderInfo.mobileNo
      || storeUserInfo.mobileNo
      || storeUserInfo.mobile
    ),
    country,
    state: country === "JP" ? normalizeConfluxJapanState(province) : province,
    city: city || district || province,
    address_line1: addressLine1,
    postal_code: normalizeValue(
      order.zipCode
      || order.postalCode
      || shippingInfo.zipCode
      || shippingInfo.postalCode
      || storedOrderInfo.zipCode
      || storedOrderInfo.postalCode
    ).replace(/\D/g, ""),
  };

  if (addressLine2) {
    addressInfo.address_line2 = addressLine2;
  }

  return addressInfo;
}

function buildConfluxBillingAddressInfoFromForm(form = {}, fallbackAddressInfo = {}) {
  const source = form && typeof form === "object" ? form : {};
  const country = normalizeConfluxCountryCode(source.countryCode || source.country || fallbackAddressInfo.country || "JP");
  const name = normalizeValue(
    source.userRealname
    || source.name
    || source.holder_name
    || [source.lastName, source.firstName].filter(Boolean).join(" ")
    || [source.last_name, source.first_name].filter(Boolean).join(" ")
  );
  const province = normalizeValue(source.province || source.state || fallbackAddressInfo.state || "");
  const city = normalizeValue(source.city || fallbackAddressInfo.city || province);
  const addressLine1 = normalizeValue(
    source.addressLine1
    || source.address_line1
    || source.street
    || source.district
    || fallbackAddressInfo.address_line1
  );
  const addressLine2 = normalizeValue(
    source.addressLine2
    || source.address_line2
    || source.detailAddress
    || source.detail
    || fallbackAddressInfo.address_line2
  );
  const billingInfo = {
    ...(name ? splitConfluxName(name) : {
      first_name: fallbackAddressInfo.first_name || "Guest",
      last_name: fallbackAddressInfo.last_name || "Guest",
    }),
    email: normalizeValue(source.email || fallbackAddressInfo.email),
    phone: normalizePhoneForConflux(source.phone || source.userMobile || fallbackAddressInfo.phone),
    country,
    state: country === "JP" ? normalizeConfluxJapanState(province) : province,
    city,
    address_line1: addressLine1,
    postal_code: normalizeValue(
      source.postalCode
      || source.postal_code
      || source.zipCode
      || fallbackAddressInfo.postal_code
    ).replace(/\D/g, ""),
  };

  if (addressLine2) {
    billingInfo.address_line2 = addressLine2;
  }
  if (source.birthDate || source.birth_date) {
    billingInfo.birth_date = normalizeValue(source.birthDate || source.birth_date);
  }

  return billingInfo;
}

function buildConfluxCardDetailsFromForm(form = {}) {
  const source = form && typeof form === "object" ? form : {};
  const expiryYear = normalizeValue(source.expiry_year || source.expiryYear);

  return {
    card_number: normalizeValue(source.card_number || source.cardNumber).replace(/\D/g, ""),
    expiry_year: expiryYear.length === 4 ? expiryYear.slice(-2) : expiryYear,
    expiry_month: normalizeValue(source.expiry_month || source.expiryMonth).padStart(2, "0"),
    cvv: normalizeValue(source.cvv || source.cvc).replace(/\D/g, ""),
    holder_name: normalizeValue(source.holder_name || source.holderName),
  };
}

function getConfluxBrowserLanguage() {
  if (!process.client || typeof navigator === "undefined") return "en";
  return `${navigator.languages?.[0] || navigator.language || "en"}`.split("-")[0] || "en";
}

function getConfluxBrowserTimezoneOffset() {
  if (!process.client) return "UTC+0";
  const offsetHour = new Date().getTimezoneOffset() / 60;
  return `UTC${offsetHour >= 0 ? "+" : ""}${offsetHour}`;
}

function buildConfluxWalletDeviceInfo(order = {}) {
  if (!process.client || typeof window === "undefined") {
    return {
      client_ip: order.userIP || "",
      language: "ja-JP",
      browser_info: {
        screen_height: 0,
        screen_width: 0,
        user_agent: "",
        time_zone: "UTC+0",
      },
    };
  }

  return {
    client_ip: order.userIP || "",
    language: "ja-JP",
    browser_info: {
      screen_height: window.screen?.height || window.innerHeight || 0,
      screen_width: window.screen?.width || window.innerWidth || 0,
      user_agent: navigator.userAgent || "",
      time_zone: getConfluxBrowserTimezoneOffset(),
    },
  };
}

function buildConfluxDeviceInfo(order = {}) {
  const walletDeviceInfo = buildConfluxWalletDeviceInfo(order);
  return {
    ...walletDeviceInfo,
    language: getConfluxBrowserLanguage(),
    browser_info: {
      ...walletDeviceInfo.browser_info,
      language: getConfluxBrowserLanguage(),
    },
    time_zone: process.client && typeof Intl !== "undefined"
      ? (Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Tokyo")
      : "Asia/Tokyo",
  };
}

function pickOrderCurrency(order = {}, store) {
  return normalizeValue(
    order.checkoutCurrency
    || order.displayCurrencyCode
    || order.currencyCode
    || order.currency
    || readStoredOrderInfo().checkoutCurrency
    || store?.getShopDefaultCurrencyCode?.()
    || "JPY"
  ).toUpperCase();
}

function resolveDefaultWalletPaymentCode() {
  if (!process.client || typeof navigator === "undefined") return "GOOGLE_PAY";

  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|Macintosh/i.test(ua) && !/CriOS|Chrome|Edg|Firefox/i.test(ua)) {
    return "APPLE_PAY";
  }
  return "GOOGLE_PAY";
}

function pickConfluxContinuePaymentMethodCode(order = {}, actionOptions = {}, route) {
  const pickedCode = normalizePaymentCode(
    actionOptions.paymentMethodCode
    || order.paymentMethodCode
    || order.payment_method_code
    || order.payOrderInfo?.paymentMethodCode
    || order.payOrderInfo?.payment_method_code
    || getRouteQueryValue(route, "paymentMethodCode")
    || readStoredOrderInfo().paymentMethodCode
  );

  if (pickedCode) {
    return pickedCode;
  }

  return resolveDefaultWalletPaymentCode();
}

function buildConfluxPaymentItemsFromOrder(order = {}) {
  const storedOrderInfo = readStoredOrderInfo();
  const details = getOrderDetails(order).length
    ? getOrderDetails(order)
    : (Array.isArray(storedOrderInfo.cartItems) ? storedOrderInfo.cartItems : []);
  const mappedItems = details.map((item, index) => {
    const qty = normalizeNumber(item.buyAmount ?? item.qty ?? item.quantity ?? item.count, 1) || 1;
    const amount = normalizeNumber(
      item.realSubtotalAmount
      ?? item.originalSubtotalAmount
      ?? item.totalAmount
      ?? item.payableAmount
      ?? item.price
      ?? item.amount,
      0
    );
    const name = normalizeValue(
      item.dealerItemSpuTitle
      || item.dealerItemSkuTitle
      || item.title
      || item.spuName
      || item.goodsName
      || item.productName
      || item.name
    ) || "item";
    const sku = normalizeValue(item.sku || item.skuCode || item.dealerItemSkuId || item.offerId || item.id || index);
    const paymentItem = {
      name,
      product_name: name,
      quantity: qty,
      amount,
    };

    if (sku) {
      paymentItem.sku = sku;
    }
    return paymentItem;
  }).filter(item => item.name && item.quantity);

  if (mappedItems.length) return mappedItems;

  return [
    {
      name: pickOrderCode(order) || pickOrderNumber(order) || "Order",
      quantity: 1,
      amount: normalizeNumber(order.realTotalAmount ?? order.payableAmount ?? order.totalAmount ?? storedOrderInfo.totalAmount, 0),
    },
  ];
}

function buildConfluxShopperReference(addressInfo = {}, order = {}, storeUserInfo = {}) {
  return normalizeValue(
    addressInfo.email
    || addressInfo.phone
    || storeUserInfo.email
    || storeUserInfo.id
    || storeUserInfo.userId
    || storeUserInfo.memberCode
    || pickOrderCode(order)
    || pickPayOrderCode(order)
    || "guest"
  );
}

function buildConfluxContinuePaymentPayload({
  order = {},
  query = {},
  methodCode = "",
  cardDetails = {},
  billingAddress = {},
  store,
}) {
  const storeUserInfo = store?.userInfo || {};
  const addressInfo = buildConfluxAddressInfoFromOrder(order, storeUserInfo);
  const resolvedMethodCode = CONFLUX_CARD_PAYMENT_CODES.includes(normalizePaymentCode(methodCode))
    ? (
        resolveConfluxCreditCardPaymentMethod(
          { code: methodCode, wayCode: methodCode, isCard: true, isGenericCreditCard: normalizePaymentCode(methodCode) === "CREDIT_CARD" },
          cardDetails.card_number || cardDetails.cardNumber
        )?.code || methodCode
      )
    : methodCode;
  const orderNumber = query.orderNumber || pickOrderNumber(order) || query.orderCode;
  const successUrl = buildAbsoluteCheckoutUrl("/payment/successful", {
    orderNumber,
    orderCode: query.orderCode,
    payOrderCode: query.payOrderCode,
    currency: pickOrderCurrency(order, store),
  });
  const pendingQuery = {
    ...query,
    pendingPay: "1",
    paymentMethodCode: resolvedMethodCode,
  };
  const cancelUrl = buildAbsoluteCheckoutUrl("/order/query", {
    ...pendingQuery,
    payCancel: "1",
  });
  const basePayload = {
    payOrderCode: query.payOrderCode || "",
    orderCode: query.orderCode || "",
    payType: 1700,
    paymentMethodCode: resolvedMethodCode,
    returnPageUrl: successUrl,
    quitUrl: cancelUrl,
    terminal: process.client && typeof window !== "undefined" && window.innerWidth > 768 ? "PC" : "H5",
    isPurchasePay: true,
    currency: pickOrderCurrency(order, store),
    shopper_reference: buildConfluxShopperReference(addressInfo, order, storeUserInfo),
    device_info: buildConfluxWalletDeviceInfo(order),
  };

  if (CONFLUX_CARD_PAYMENT_CODES.includes(normalizePaymentCode(resolvedMethodCode))) {
    const billingInfo = buildConfluxBillingAddressInfoFromForm(billingAddress, addressInfo);

    return {
      ...basePayload,
      shipping_info: addressInfo,
      billing_info: billingInfo,
      card_details: buildConfluxCardDetailsFromForm(cardDetails),
      shopper_reference: buildConfluxShopperReference(billingInfo, order, storeUserInfo),
      device_info: buildConfluxDeviceInfo(order),
      channel_params: {},
      items: buildConfluxPaymentItemsFromOrder(order),
    };
  }

  return basePayload;
}

function saveConfluxContinuePendingQuery(query = {}) {
  if (!process.client) return;

  try {
    sessionStorage.setItem("japan_conflux_payment_in_flight", "1");
    localStorage.setItem("japan_conflux_pending_payment_query", JSON.stringify(query));
    saveJapanPendingPaymentRedirect(query, "/order/query");
  } catch (error) {
    // Storage is only a resume helper.
  }
}

async function handleConfluxContinuePaymentResponse({
  response = {},
  query = {},
  router,
}) {
  if (response?.success === false) {
    throw new Error(response.message || response.msg || $t("payment.createOnlinePaymentFailed"));
  }

  if (process.client) {
    try {
      localStorage.setItem("confluxPaymentResponse", JSON.stringify(response?.data ?? response ?? {}));
    } catch (error) {
      // Ignore storage failures so navigation can continue.
    }
  }

  const result = resolveConfluxPaymentResult(response);
  saveConfluxContinuePendingQuery({
    ...query,
    pendingPay: "1",
    payCancel: "1",
  });

  if (result.redirectUrl && process.client) {
    window.location.href = result.redirectUrl;
    return;
  }

  if (result.html && process.client) {
    document.open();
    document.write(result.html);
    document.close();
    return;
  }

  if (result.qrCode && process.client) {
    localStorage.setItem("confluxPaymentQrCode", result.qrCode);
    ElMessage.success($t("payment.scanQrToPay"));
    return;
  }

  ElMessage.success($t("payment.onlinePaymentAccepted"));
  await router.push({
    path: "/payment/successful",
    query: {
      orderNumber: query.orderNumber || query.orderCode,
      orderCode: query.orderCode,
      payOrderCode: query.payOrderCode,
    },
  });
}

function pickLegacyOrderId(order = {}) {
  return normalizeValue(order.orderId || order.mallOrderId || order.id || order.dealerOrderPrimaryId);
}

function buildLegacyPayOrderPayload(order = {}) {
  const dealerOrderPrimaryId = pickOrderId(order);
  const orderId = pickLegacyOrderId(order);
  const version = normalizeValue(order.version || order.orderVersion || "1.0");

  return {
    mallOrderDTOList: [
      {
        dealerOrderPrimaryId,
        orderId,
        version,
      },
    ],
    orderId,
    version,
  };
}

function buildCashierQuery(order = {}, options = {}) {
  const query = {
    id: pickOrderId(order),
    orderCode: pickOrderCode(order),
    payOrderCode: pickPayOrderCode(order),
  };

  if (options.publicOrder) {
    query.public = "1";
  }

  return Object.entries(query).reduce((result, [key, value]) => {
    if (value) result[key] = value;
    return result;
  }, {});
}

export function useContinueOrderPayment(options = {}) {
  const router = useRouter();
  const route = useRoute();
  const store = useMainStore();
  const siteTitle = computed(() => resolveSiteDisplayTitle(store.getSiteInfo()));
  const { checkout } = useSiteTemplate();
  const { isJapanStyle } = useJapanStyleSite();
  const checkoutConfig = computed(() => resolveTemplateCheckoutConfig(checkout.value));
  const continuePaymentConfig = computed(() => checkoutConfig.value.continuePayment || {});
  const shouldUseCashier = computed(() => {
    return continuePaymentConfig.value.mode === "cashier"
      && continuePaymentConfig.value.provider === "conflux";
  });
  const shouldUseDirectConfluxPayment = computed(() => {
    return isJapanStyle.value && continuePaymentConfig.value.provider === "conflux";
  });
  const shouldUsePaymentPage = computed(() => {
    return !isJapanStyle.value && (
      continuePaymentConfig.value.mode === "payment"
      && continuePaymentConfig.value.provider === "conflux"
    );
  });

  async function payLegacyOrder(order = {}) {
    const response = await payOrder(buildLegacyPayOrderPayload(order));

    if (response?.success) {
      ElMessage.success($t("texts.paymentSuccess"));
      if (typeof options.onLegacyPaid === "function") {
        await options.onLegacyPaid(response, order);
      }
      return response;
    }

    throw new Error(response?.msg || response?.message || $t("texts.paymentFailed"));
  }

  async function continueOrderPayment(order = {}, actionOptions = {}) {
    if (shouldUseDirectConfluxPayment.value) {
      const query = buildPaymentResumeQuery(order, actionOptions);

      if (!query.orderCode || !query.payOrderCode) {
        ElMessage.error($t("texts.paymentFailed"));
        return null;
      }

      const methodCode = pickConfluxContinuePaymentMethodCode(order, actionOptions, route);
      const pendingQuery = {
        ...query,
        paymentMethodCode: methodCode,
      };

      persistPaymentResumeSnapshot(order, pendingQuery, siteTitle.value);
      saveConfluxContinuePendingQuery({
        ...pendingQuery,
        pendingPay: "1",
        payCancel: "1",
      });

      try {
        const response = await createConfluxPaymentOrder(buildConfluxContinuePaymentPayload({
          order,
          query: pendingQuery,
          methodCode,
          cardDetails: actionOptions.cardDetails,
          billingAddress: actionOptions.billingAddress,
          store,
        }));

        await handleConfluxContinuePaymentResponse({
          response,
          query: pendingQuery,
          router,
        });

        return {
          success: true,
          redirectedToConflux: true,
          response,
        };
      } catch (error) {
        ElMessage.error(error?.message || $t("payment.createOnlinePaymentFailed"));
        saveJapanPendingPaymentRedirect({
          ...pendingQuery,
          payFailed: "1",
        }, "/order/query");
        return null;
      }
    }

    if (shouldUsePaymentPage.value) {
      const paymentPath = continuePaymentConfig.value.paymentPath || "/payment";
      const query = buildPaymentResumeQuery(order, actionOptions);

      if (!query.orderCode && !query.orderNumber && !query.payOrderCode) {
        ElMessage.error($t("texts.paymentFailed"));
        return null;
      }

      persistPaymentResumeSnapshot(order, query, siteTitle.value);
      saveJapanPendingPaymentRedirect(query, paymentPath);

      await router.push({
        path: paymentPath,
        query,
      });

      return {
        success: true,
        redirectedToPayment: true,
      };
    }

    if (shouldUseCashier.value) {
      const cashierPath = continuePaymentConfig.value.cashierPath || "/sub/payment/cashier";
      const query = buildCashierQuery(order, actionOptions);

      if (!query.id && !query.orderCode) {
        ElMessage.error($t("texts.paymentFailed"));
        return null;
      }

      await router.push({
        path: cashierPath,
        query,
      });

      return {
        success: true,
        redirectedToCashier: true,
      };
    }

    try {
      return await payLegacyOrder(order);
    } catch (error) {
      ElMessage.error(error?.message || $t("texts.paymentFailed"));
      return null;
    }
  }

  return {
    checkoutConfig,
    continuePaymentConfig,
    shouldUseCashier,
    shouldUseDirectConfluxPayment,
    shouldUsePaymentPage,
    continueOrderPayment,
  };
}

export default useContinueOrderPayment;
